import React from 'react';
import PublicPageShell from './PublicPageShell';

export default function PrivacyScreen() {
  return (
    <PublicPageShell title="Privacy Policy" updated="September 4, 2026">
      <p>
        Grove — Family Ops Hub ("Grove," "we," "us") is a family logistics
        app available at <strong>grove.mcschaef.family</strong>. This
        policy explains what information Grove collects, how it's used, and
        the choices you have. Grove is built for a small number of
        households and their invited caregivers — we do not sell data, run
        advertising, or share family information with data brokers.
      </p>

      <h2>1. Information we collect</h2>
      <p>When you sign in and use Grove, we collect:</p>
      <ul>
        <li>
          <strong>Google account information.</strong> When you sign in with
          Google, we receive your name, email address, and profile photo, as
          provided by Google Sign-In.
        </li>
        <li>
          <strong>Google Calendar data.</strong> If you connect a Google
          Calendar, Grove reads calendar events (titles, times, and
          attendees) to build the household schedule view, and — only when
          you take an explicit action in the app, such as assigning a
          pickup — writes or updates events on your behalf.
        </li>
        <li>
          <strong>Family information you enter.</strong> This includes
          household member names, children's names and care notes, house
          and emergency-contact details, sitter/caregiver contact info, and
          any other content a family chooses to store in Grove.
        </li>
        <li>
          <strong>Basic usage data.</strong> Standard technical data such as
          browser type and error logs, used only to keep the app working
          and to diagnose bugs.
        </li>
      </ul>
      <p>
        Grove does not access Gmail, and does not collect payment
        information — Grove is currently offered free of charge.
      </p>

      <h2>2. How we use information</h2>
      <ul>
        <li>To show your household's schedule, caregivers, and reference info inside Grove.</li>
        <li>To let account owners grant scoped access to invited caregivers (sitters, grandparents, etc.).</li>
        <li>To keep the app secure, diagnose problems, and improve reliability.</li>
        <li>To communicate with you about your account, if needed (e.g. a security notice).</li>
      </ul>
      <p>We do not use your data to serve ads, and we do not sell or rent personal information to third parties.</p>

      <h2>3. Google user data &amp; Limited Use</h2>
      <p>
        Grove's use and transfer to any other app of information received
        from Google APIs will adhere to the{' '}
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noreferrer"
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements. Calendar data obtained
        through Google APIs is used solely to provide and improve the
        family-scheduling features described in this policy — it is never
        used for advertising, sold, or shared with third parties other than
        the caregivers your household explicitly invites into your Grove
        account.
      </p>

      <h2>4. Children's information</h2>
      <p>
        Grove is used by parents and guardians to store logistics
        information about their own children (such as a first name, care
        notes, or a school pickup time). This information is entered by the
        adult account owner, not collected directly from children. If you
        are a parent or guardian and want information about your child
        removed from Grove, contact us using the details below.
      </p>

      <h2>5. Data storage &amp; security</h2>
      <p>
        Family data is stored in Google Firebase / Firestore, a
        Google Cloud service, and is protected by access-control rules that
        restrict each family's data to that family's owner and the
        caregivers they've explicitly authorized. Access to shared caregiver
        views is controlled through unique, revocable links or invited
        Google accounts — Grove does not make any family's data public.
      </p>

      <h2>6. Data sharing</h2>
      <p>
        We do not sell, rent, or share your personal information with third
        parties for their own marketing purposes. Information is shared
        only:
      </p>
      <ul>
        <li>within your household, with caregivers you've explicitly invited or linked;</li>
        <li>with service providers that operate Grove's infrastructure (Google Firebase/Cloud), solely to run the app;</li>
        <li>if required by law, or to protect the rights, safety, or property of Grove or its users.</li>
      </ul>

      <h2>7. Data retention &amp; deletion</h2>
      <p>
        We retain family data for as long as the account is active. You can
        remove individual records (e.g. a caregiver, a child's note) directly
        in the app. To request deletion of your entire account and
        associated family data, contact us using the details below and we
        will delete it within a reasonable time.
      </p>

      <h2>8. Your choices</h2>
      <ul>
        <li>
          You can revoke Grove's access to your Google account at any time
          from your{' '}
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer">
            Google Account permissions
          </a>{' '}
          page.
        </li>
        <li>Account owners can revoke or expire caregiver share links at any time from within Grove.</li>
        <li>You may request a copy or deletion of your data by contacting us.</li>
      </ul>

      <h2>9. Changes to this policy</h2>
      <p>
        If we make material changes to this policy, we'll update the "last
        updated" date above. Continued use of Grove after changes are
        posted means you accept the revised policy.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions about this policy or your data? Contact the Grove account
        owner at{' '}
        <a href="mailto:adreck.mcdonald@gmail.com">adreck.mcdonald@gmail.com</a>.
      </p>
    </PublicPageShell>
  );
}
